// ─────────────────────────────────────────────────────────────────────────────
// «افزودن از فهرست خدمات» — the catalogue-first composer for a قالب دستورات.
//
// WHAT A TEMPLATE IS, AND WHY THIS IS NOT A FORM
//
// A قالب دستورات is a برگه درخواست written in advance: an ORDERED LIST of
// services, each of which has to be identifiable by something other than the
// words a doctor typed. The old editor asked for six inputs per line — type,
// name, urgency, code, sessions, note — all at equal weight, all always open.
// Four of those six are almost always left at their default, and the one that
// mattered most (`code`) was a free-text box, which is the only way a wrong
// code ever gets into this system by hand.
//
// So the section is built the other way round: SEARCH the catalogue, pick, and
// the line arrives already identified. Typing a name by hand is still possible —
// a clinic offers services no national list has heard of — but it is a separate,
// deliberate button rather than the default path.
//
// THE CODE CHIP IS THE POINT
//
// Checking Parsina's catalogue against مکسا found four codes that EXIST but name
// a different test — 35765-7 reads «Vitamin D» here and «Cefetamet
// susceptibility» in the registry. A bare number cannot be checked by eye. So a
// code is never shown bare: it is always «REGISTRY number», in tabular Latin
// figures, and a line with no identity says «بدون کد» instead of showing an
// empty field. What a line can be transmitted as is visible at a glance.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useRef, useState } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import { SearchInput, Button } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { ORDER_TYPE_KEYS } from '@parsina/framework/clinical/src/constants/patientRecord';
import { LTR, R_CONTROL } from './SettingsKit';
import { useOrderCatalogSearch } from './TemplateOrderPicker';

const MAX_RESULTS = 24;
const TYPE_FILTERS = ['all', 'lab', 'imaging', 'procedure'];

/**
 * Which registry a code belongs to. Derived from the order type today, because
 * that is all the BFF returns: LOINC for labs, CPT for procedures, nothing for
 * imaging. When the مکسا identity (LAB-ir / Radiology-ir / RVU3) reaches the
 * GraphQL layer, a row carries its own system and this falls back to it.
 */
export const codeSystemFor = (item) => item?.codeSystem
  || (item?.orderType === 'lab' ? 'LOINC' : item?.orderType === 'procedure' ? 'CPT' : '');

/**
 * The identity of a line, or the honest absence of one.
 *
 * Three states, and they are the three things a doctor needs to know before this
 * template is applied to a real visit:
 *   linked + code  — teal, the registry named beside the number
 *   linked, no code — neutral; imaging carries neither LOINC nor CPT in this schema
 *   typed by hand  — dashed, «دستی»; nothing downstream can match it
 */
export function OrderCodeChip({ item, size = 'md' }) {
  const { t } = useTranslation();
  const system = codeSystemFor(item);
  const linked = !!item?.catalogId;
  const pad = size === 'sm' ? '1px 6px' : '2px 8px';
  const fs = size === 'sm' ? '0.625rem' : '0.65625rem';

  if (item?.code) {
    return (
      <span
        data-testid="order-code-chip"
        style={{
          ...LTR, display: 'inline-flex', alignItems: 'baseline', gap: 4, padding: pad,
          borderRadius: 6, background: soft(C.teal, 0.12), color: C.teal,
          fontSize: fs, fontWeight: 700, whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em',
        }}
      >
        {system && <span style={{ opacity: 0.75, fontWeight: 800, fontSize: `calc(${fs} - 0.0625rem)` }}>{system}</span>}
        <span>{item.code}</span>
      </span>
    );
  }
  return (
    <span
      data-testid="order-code-chip"
      style={{
        display: 'inline-flex', alignItems: 'center', padding: pad, borderRadius: 6,
        border: linked ? 'none' : `1px dashed ${C.border}`,
        background: linked ? soft(C.navy, 0.06) : 'transparent',
        color: C.textMuted, fontSize: fs, fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      {t(linked ? 'settings.templates.orderNoCode' : 'settings.templates.orderManual')}
    </span>
  );
}

/** A quiet tile carrying the type, so the eye can sort a long list by shape. */
export function OrderTypeTile({ orderType, fromCatalogue = false }) {
  const { t } = useTranslation();
  const label = t(ORDER_TYPE_KEYS[orderType] || ORDER_TYPE_KEYS.other);
  return (
    <span
      title={fromCatalogue ? `${label} — ${t('settings.templates.orderTypeFromCatalogue')}` : label}
      style={{
        flexShrink: 0, minWidth: 44, padding: '2px 7px', borderRadius: 6,
        background: soft(C.navy, 0.07), color: C.navy,
        fontSize: '0.625rem', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap',
      }}
    >{label}</span>
  );
}

const norm = (s) => String(s || '').toLowerCase().trim();

/**
 * @param onPick     (item) => void — appends a line, already identified
 * @param onManual   () => void — appends an empty line to type into
 * @param excludeIds Set of catalogue ids already in the draft
 * @param disabled   at the item limit
 */
export default function TemplateOrderComposer({ onPick, onManual, excludeIds, disabled = false }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const inputRef = useRef(null);

  // The server searches; this only decides what to show. The type chips are a
  // QUERY PARAMETER now, not a client filter over a resident list.
  const { items, total, loading, error } = useOrderCatalogSearch({
    query, orderType: type === 'all' ? '' : type, enabled: !disabled,
  });
  const results = useMemo(() => items.slice(0, MAX_RESULTS), [items]);

  const searching = norm(query).length > 0;

  return (
    <div data-testid="template-order-composer" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <SearchInput
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e?.target ? e.target.value : e)}
            placeholder={t('settings.templates.orderSearchPlaceholder')}
            disabled={disabled}
            inputProps={{ 'data-testid': 'template-order-search' }}
          />
        </div>
        <Button
          variant="subtle" size="sm" onClick={onManual} disabled={disabled}
          data-testid="template-add-item"
        >
          + {t('settings.templates.addManual')}
        </Button>
      </div>

      {/* The type filter narrows a 341-service list to the shelf the doctor is
          thinking about. «همه» stays first and selected, so it never becomes a
          step you must take before searching. */}
      <div role="group" aria-label={t('settings.templates.order_.type')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {TYPE_FILTERS.map((key) => {
          const on = type === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setType(key)}
              data-testid={`template-order-filter-${key}`}
              aria-pressed={on}
              style={{
                height: 26, padding: '0 10px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${on ? soft(C.navy, 0.35) : C.border}`,
                background: on ? soft(C.navy, 0.08) : 'transparent',
                color: on ? C.navy : C.textSecondary,
                fontFamily: 'inherit', fontSize: '0.71875rem', fontWeight: on ? 800 : 600,
              }}
            >
              {key === 'all' ? t('common.state.all') : t(ORDER_TYPE_KEYS[key])}
            </button>
          );
        })}
      </div>

      {searching && (
        <div
          data-testid="template-order-results"
          style={{
            border: `1px solid ${C.border}`, borderRadius: R_CONTROL, background: C.card,
            padding: 4, maxHeight: 250, overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 2,
          }}
        >
          {loading ? (
            <div style={{ padding: '10px 12px', fontSize: '0.75rem', color: C.textMuted }}>{t('common.state.loading')}</div>
          ) : error ? (
            <div role="alert" data-testid="template-order-error" style={{ padding: '10px 12px', fontSize: '0.75rem', color: C.red }}>
              {t('settings.templates.orderCatalogFailed')}
            </div>
          ) : results.length === 0 ? (
            <div data-testid="template-order-empty" style={{ padding: '10px 12px', fontSize: '0.75rem', color: C.textMuted, lineHeight: 1.9 }}>
              {t('settings.templates.orderCatalogEmpty')}
            </div>
          ) : results.map((item) => {
            const already = excludeIds?.has(item.catalogId);
            return (
              <button
                key={`${item.orderType}:${item.catalogId}`}
                type="button"
                onClick={() => onPick(item)}
                disabled={disabled || already}
                data-testid="template-order-result"
                data-catalog-id={item.catalogId || ''}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 8px', borderRadius: 8, border: 'none', textAlign: 'start',
                  background: 'transparent', fontFamily: 'inherit',
                  cursor: disabled || already ? 'default' : 'pointer', opacity: already ? 0.55 : 1,
                }}
                onMouseEnter={(e) => { if (!already && !disabled) e.currentTarget.style.background = soft(C.navy, 0.05); }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <OrderTypeTile orderType={item.orderType} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span dir="auto" style={{ display: 'block', fontSize: '0.78125rem', fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name}
                  </span>
                  {item.category && (
                    <span style={{ display: 'block', fontSize: '0.65625rem', color: C.textMuted }}>{item.category}</span>
                  )}
                </span>
                <OrderCodeChip item={item} size="sm" />
                <span style={{ fontSize: '0.71875rem', fontWeight: 800, color: already ? C.textMuted : C.navy, flexShrink: 0 }}>
                  {already ? t('settings.templates.alreadyInTemplate') : '+'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
